import React, { useState, useEffect, useCallback } from 'react';
import { cpGetHeaders, cpGetAuthHeader } from './ClientPortalApp';
import { openFilePreview } from '../common/FilePreviewModal';
import Select2Dropdown from '../common/Select2Dropdown';

const API = '/api/client/jobs/';

const formatDate = (v) => (v ? new Date(v).toLocaleDateString() : '-');

const StatusBadges = ({ statuses }) => {
    if (!statuses || statuses.length === 0) return <span className="cp-badge">No Status</span>;
    return (
        <span style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {statuses.map((s, i) => (
                <span key={i} className="cp-badge cp-badge-blue">{s}</span>
            ))}
        </span>
    );
};

// ── Job Detail Modal ──────────────────────────────────────────────────────────
const JobDetailModal = ({ jobId, onClose }) => {
    const [job, setJob] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetch(`${API}${jobId}/`, { headers: cpGetAuthHeader() })
            .then(r => r.ok ? r.json() : null)
            .then(data => { setJob(data); setLoading(false); })
            .catch(() => setLoading(false));
    }, [jobId]);

    if (loading) return (
        <div className="cp-modal-overlay">
            <div className="cp-modal"><div className="cp-loader"><i className="fa-solid fa-circle-notch fa-spin"></i> Loading...</div></div>
        </div>
    );

    if (!job) return null;

    const field = (label, value) => (
        <div className="cp-detail-field" key={label}>
            <label>{label}</label>
            <div className="value">{value || '-'}</div>
        </div>
    );

    return (
        <div className="cp-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
            <div className="cp-modal">
                <div className="cp-modal-header">
                    <h3>{job.kk_file_no || `Job #${job.id}`}</h3>
                    <button className="cp-modal-close" onClick={onClose}>&times;</button>
                </div>
                <div className="cp-modal-body">
                    {/* Status */}
                    <div style={{ marginBottom: 16 }}>
                        <StatusBadges statuses={job.status} />
                        {job.status_remark && (
                            <div style={{ marginTop: 6, fontSize: '0.85rem', color: '#64748b' }}>{job.status_remark}</div>
                        )}
                    </div>

                    {/* Core details */}
                    <div className="cp-section-title">Shipment Details</div>
                    <div className="cp-detail-grid">
                        {field('KK File No', job.kk_file_no)}
                        {field('Shipment Type', job.shipment_type)}
                        {field('Cargo Type', job.cargo_type)}
                        {field('Shipping Line', job.shipping_line_name)}
                        {field('BL Type', job.bl_type)}
                        {field('MBL No', job.mbl_no)}
                        {field('HBL/SO No', job.hbl_so_no)}
                        {field('IDF No', job.idf_no)}
                        {field('TANSAD No', job.tansad_no)}
                        {field('TANSAD Date', formatDate(job.tansad_date))}
                        {field('UCR No', job.ucr_no)}
                        {field('Invoice No', job.invoice_no)}
                        {field('Customer Ref', job.customer_ref)}
                        {field('Supplier', job.supplier_name)}
                        {field('Receipt Date', formatDate(job.receipt_date))}
                        {field('DO Received', formatDate(job.do_received_date))}
                        {field('DO Expiry', formatDate(job.do_expiry_date))}
                        {field('Discharge Date', formatDate(job.discharge_date))}
                        {field('Release Date', formatDate(job.release_date))}
                        {field('Drop Off Date', formatDate(job.drop_off_date))}
                        {field('Duty Paid', job.duty_paid ? `Yes — ${formatDate(job.duty_paid_date)}` : 'No')}
                    </div>

                    {/* Containers */}
                    {job.containers && job.containers.length > 0 && (
                        <>
                            <div className="cp-section-title">Containers</div>
                            <div className="cp-table-wrap" style={{ marginBottom: 16 }}>
                                <table className="cp-table">
                                    <thead>
                                        <tr>
                                            <th>Container No</th>
                                            <th>Seal No</th>
                                            <th>Size</th>
                                            <th>Type</th>
                                            <th>Packages</th>
                                            <th>Gross Weight</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {job.containers.map(c => (
                                            <tr key={c.id}>
                                                <td>{c.container_no || '-'}</td>
                                                <td>{c.seal_no || '-'}</td>
                                                <td>{c.size || '-'}</td>
                                                <td>{c.container_type || '-'}</td>
                                                <td>{c.packages ?? '-'}</td>
                                                <td>{c.gross_weight ?? '-'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}

                    {/* Commodities */}
                    {job.commodities && job.commodities.length > 0 && (
                        <>
                            <div className="cp-section-title">Commodities</div>
                            <div className="cp-table-wrap" style={{ marginBottom: 16 }}>
                                <table className="cp-table">
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>HS Code</th>
                                            <th>Description</th>
                                            <th>Qty</th>
                                            <th>Unit Price</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {job.commodities.map(c => (
                                            <tr key={c.id}>
                                                <td>{c.item_no || '-'}</td>
                                                <td>{c.hs_code || '-'}</td>
                                                <td>{c.description || '-'}</td>
                                                <td>{c.quantity ?? '-'} {c.quantity_unit || ''}</td>
                                                <td>{c.unit_price ?? '-'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}

                    {/* Documents */}
                    {job.documents && job.documents.length > 0 && (
                        <>
                            <div className="cp-section-title">Documents</div>
                            <div className="cp-table-wrap">
                                <table className="cp-table">
                                    <thead>
                                        <tr>
                                            <th>Type</th>
                                            <th>Doc No</th>
                                            <th>Issue Date</th>
                                            <th>Expiry Date</th>
                                            <th>View</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {job.documents.map(d => (
                                            <tr key={d.id}>
                                                <td>{d.document_type || '-'}</td>
                                                <td>{d.document_no || '-'}</td>
                                                <td>{formatDate(d.issue_date)}</td>
                                                <td>{formatDate(d.expiry_date)}</td>
                                                <td>
                                                    {d.file_url && (
                                                        <button
                                                            className="cp-btn-icon"
                                                            title="View document"
                                                            onClick={() => openFilePreview(d.file_url, d.document_type || 'Document')}
                                                        >
                                                            <i className="fa-solid fa-eye"></i>
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </div>
                <div className="cp-modal-footer">
                    <button className="cp-btn cp-btn-secondary" onClick={onClose}>Close</button>
                </div>
            </div>
        </div>
    );
};

// ── Main page ─────────────────────────────────────────────────────────────────
const ClientDailyReport = () => {
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchQ, setSearchQ] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [shipmentType, setShipmentType] = useState('');
    const [viewJobId, setViewJobId] = useState(null);

    const fetchJobs = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (searchQ) params.append('q', searchQ);
            if (startDate) params.append('start_date', startDate);
            if (endDate) params.append('end_date', endDate);
            if (shipmentType) params.append('shipment_type', shipmentType);
            const res = await fetch(`${API}?${params.toString()}`, { headers: cpGetAuthHeader() });
            const data = await res.json();
            setJobs(data.results || data || []);
        } catch {
            setJobs([]);
        } finally {
            setLoading(false);
        }
    }, [searchQ, startDate, endDate, shipmentType]);

    useEffect(() => {
        fetchJobs();
    }, []);

    const handleSearch = (e) => {
        e.preventDefault();
        fetchJobs();
    };

    const SHIPMENT_TYPES = [
        '', 'IMPORT-SEA', 'IMPORT-AIR', 'IMPORT-ROAD',
        'EXPORT-SEA', 'EXPORT-AIR', 'EXPORT-ROAD',
        'TRANSIT-SEA', 'TRANSIT-AIR',
    ];

    const SHIPMENT_TYPE_OPTIONS = [
        { label: 'All Types', value: '' },
        ...SHIPMENT_TYPES.filter(t => t).map(t => ({ label: t, value: t })),
    ];

    return (
        <>
            <div className="cp-page-header">
                <h2>Daily Jobs Report</h2>
            </div>

            <div className="cp-card">
                <form onSubmit={handleSearch}>
                    <div className="cp-filters">
                        <div className="cp-filter-group">
                            <label>Search</label>
                            <input
                                type="text"
                                placeholder="KK No, IDF, TANSAD, UCR, MBL…"
                                value={searchQ}
                                onChange={e => setSearchQ(e.target.value)}
                            />
                        </div>
                        <div className="cp-filter-group">
                            <label>Shipment Type</label>
                            <Select2Dropdown
                                options={SHIPMENT_TYPE_OPTIONS}
                                value={shipmentType}
                                onChange={v => setShipmentType(v)}
                                placeholder="All Types"
                                displayKey="label"
                                valueKey="value"
                                searchable={false}
                                menuPlacement="auto"
                            />
                        </div>
                        <div className="cp-filter-group">
                            <label>From Date</label>
                            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
                        </div>
                        <div className="cp-filter-group">
                            <label>To Date</label>
                            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
                        </div>
                        <button type="submit" className="cp-btn cp-btn-primary" style={{ alignSelf: 'flex-end' }}>
                            <i className="fa-solid fa-search"></i> Search
                        </button>
                    </div>
                </form>
            </div>

            <div className="cp-table-wrap">
                {loading ? (
                    <div className="cp-loader"><i className="fa-solid fa-circle-notch fa-spin"></i> Loading…</div>
                ) : jobs.length === 0 ? (
                    <div className="cp-empty">No jobs found.</div>
                ) : (
                    <table className="cp-table">
                        <thead>
                            <tr>
                                <th>KK File No</th>
                                <th>Shipment Type</th>
                                <th>IDF No</th>
                                <th>TANSAD No</th>
                                <th>UCR No</th>
                                <th>Invoice No</th>
                                <th>Shipping Line</th>
                                <th>Status</th>
                                <th>Created</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {jobs.map(job => (
                                <tr key={job.id}>
                                    <td>{job.kk_file_no || '-'}</td>
                                    <td>{job.shipment_type || '-'}</td>
                                    <td>{job.idf_no || '-'}</td>
                                    <td>{job.tansad_no || '-'}</td>
                                    <td>{job.ucr_no || '-'}</td>
                                    <td>{job.invoice_no || '-'}</td>
                                    <td>{job.shipping_line_name || '-'}</td>
                                    <td><StatusBadges statuses={job.status} /></td>
                                    <td>{formatDate(job.created_at)}</td>
                                    <td>
                                        <button
                                            className="cp-btn-icon"
                                            title="View details"
                                            onClick={() => setViewJobId(job.id)}
                                        >
                                            <i className="fa-solid fa-eye"></i>
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {viewJobId && (
                <JobDetailModal jobId={viewJobId} onClose={() => setViewJobId(null)} />
            )}
        </>
    );
};

export default ClientDailyReport;
